import type { EnglishLesson } from '../../lib/types';

interface VocabItem {
  en: string;
  fa: string;
  example: string;
}

interface VocabPack {
  title: string;
  titleFa: string;
  instruction: string;
  items: VocabItem[];
  exercisePrompt: string;
  exerciseOptions: string[];
  exerciseAnswer: string;
}

interface GrammarPack {
  title: string;
  titleFa: string;
  instruction: string;
  content: string;
  prompt: string;
  answer: string;
}

interface ReadingPack {
  title: string;
  titleFa: string;
  instruction: string;
  content: string;
  prompt: string;
  options: string[];
  answer: string;
}

interface WritingPack {
  title: string;
  titleFa: string;
  instruction: string;
  content: string;
  prompt: string;
  hints: string[];
}

interface ReviewPack {
  title: string;
  titleFa: string;
  content: string;
  prompt: string;
  options: string[];
  answer: string;
}

interface EnglishWeekPlan {
  week: number;
  themeFa: string;
  vocabOne: VocabPack;
  grammarOne: GrammarPack;
  vocabTwo: VocabPack;
  reading: ReadingPack;
  grammarTwo: GrammarPack;
  writing: WritingPack;
  review: ReviewPack;
}

const formatVocab = (items: VocabItem[]): string =>
  items
    .map((item) => `${item.en} — ${item.fa}\nمثال: ${item.example}`)
    .join('\n\n');

const buildWeekLessons = (plan: EnglishWeekPlan): EnglishLesson[] => {
  const week = String(plan.week).padStart(2, '0');

  return [
    {
      id: `en-w${week}-d01-vocab-a`,
      title: `Week ${plan.week}: ${plan.vocabOne.title}`,
      titleFa: `هفته ${plan.week}: ${plan.vocabOne.titleFa}`,
      type: 'vocab',
      instruction: plan.vocabOne.instruction,
      content: formatVocab(plan.vocabOne.items),
      exercise: {
        type: 'multiple-choice',
        prompt: plan.vocabOne.exercisePrompt,
        options: plan.vocabOne.exerciseOptions,
        correctAnswer: plan.vocabOne.exerciseAnswer,
      },
    },
    {
      id: `en-w${week}-d02-grammar-a`,
      title: `Week ${plan.week}: ${plan.grammarOne.title}`,
      titleFa: `هفته ${plan.week}: ${plan.grammarOne.titleFa}`,
      type: 'grammar',
      instruction: plan.grammarOne.instruction,
      content: plan.grammarOne.content,
      exercise: {
        type: 'fill-blank',
        prompt: plan.grammarOne.prompt,
        correctAnswer: plan.grammarOne.answer,
      },
    },
    {
      id: `en-w${week}-d03-vocab-b`,
      title: `Week ${plan.week}: ${plan.vocabTwo.title}`,
      titleFa: `هفته ${plan.week}: ${plan.vocabTwo.titleFa}`,
      type: 'vocab',
      instruction: plan.vocabTwo.instruction,
      content: formatVocab(plan.vocabTwo.items),
      exercise: {
        type: 'multiple-choice',
        prompt: plan.vocabTwo.exercisePrompt,
        options: plan.vocabTwo.exerciseOptions,
        correctAnswer: plan.vocabTwo.exerciseAnswer,
      },
    },
    {
      id: `en-w${week}-d04-reading`,
      title: `Week ${plan.week}: ${plan.reading.title}`,
      titleFa: `هفته ${plan.week}: ${plan.reading.titleFa}`,
      type: 'reading',
      instruction: plan.reading.instruction,
      content: plan.reading.content,
      exercise: {
        type: 'multiple-choice',
        prompt: plan.reading.prompt,
        options: plan.reading.options,
        correctAnswer: plan.reading.answer,
      },
    },
    {
      id: `en-w${week}-d05-grammar-b`,
      title: `Week ${plan.week}: ${plan.grammarTwo.title}`,
      titleFa: `هفته ${plan.week}: ${plan.grammarTwo.titleFa}`,
      type: 'grammar',
      instruction: plan.grammarTwo.instruction,
      content: plan.grammarTwo.content,
      exercise: {
        type: 'fill-blank',
        prompt: plan.grammarTwo.prompt,
        correctAnswer: plan.grammarTwo.answer,
      },
    },
    {
      id: `en-w${week}-d06-writing`,
      title: `Week ${plan.week}: ${plan.writing.title}`,
      titleFa: `هفته ${plan.week}: ${plan.writing.titleFa}`,
      type: 'writing',
      instruction: plan.writing.instruction,
      content: plan.writing.content,
      exercise: {
        type: 'writing-prompt',
        prompt: plan.writing.prompt,
        hints: plan.writing.hints,
      },
    },
    {
      id: `en-w${week}-d07-review`,
      title: `Week ${plan.week}: ${plan.review.title}`,
      titleFa: `هفته ${plan.week}: ${plan.review.titleFa}`,
      type: 'grammar',
      instruction:
        `مرور هفتگی ${plan.themeFa}: نکات واژگان و دستور این هفته را یک بار دیگر بخوانید و سپس به سوال پاسخ دهید.`,
      content: plan.review.content,
      exercise: {
        type: 'multiple-choice',
        prompt: plan.review.prompt,
        options: plan.review.options,
        correctAnswer: plan.review.answer,
      },
    },
  ];
};

const englishWeekPlans: EnglishWeekPlan[] = [
  {
    week: 1,
    themeFa: 'شروع ارتباط روزمره',
    vocabOne: {
      title: 'Everyday Greetings',
      titleFa: 'سلام و احوال پرسی روزمره',
      instruction:
        'اول معنی واژه ها را بخوانید. بعد مثال ها را با صدای بلند تکرار کنید تا عبارت ها در مکالمه روزمره برایتان طبیعی شود.',
      items: [
        { en: 'Hello', fa: 'سلام', example: 'Hello, my name is Neda.' },
        { en: 'How are you?', fa: 'حالت چطور است؟', example: 'How are you today?' },
        { en: 'I am fine.', fa: 'من خوبم.', example: 'I am fine, thank you.' },
        { en: 'Nice to meet you.', fa: 'از آشنایی شما خوشحالم.', example: 'Nice to meet you, Sara.' },
        { en: 'See you later.', fa: 'بعدا می بینمت.', example: 'See you later at class.' },
      ],
      exercisePrompt: 'کدام گزینه معنی «Nice to meet you.» است؟',
      exerciseOptions: ['شب بخیر', 'از آشنایی شما خوشحالم', 'من عجله دارم', 'کلاس تمام شد'],
      exerciseAnswer: 'از آشنایی شما خوشحالم',
    },
    grammarOne: {
      title: 'Verb To Be Basics',
      titleFa: 'پایه فعل to be',
      instruction:
        'در این درس یاد می گیرید am, is, are را با ضمیر درست به کار ببرید. اول جدول را بخوانید، سپس جمله ها را مقایسه کنید.',
      content:
        'I am a mother.\nYou are kind.\nHe is at home.\nShe is a teacher.\nWe are neighbors.\nThey are ready.\n\nنکته: am فقط با I می آید. is با he/she و are با you/we/they.',
      prompt: 'We ___ ready for class.',
      answer: 'are',
    },
    vocabTwo: {
      title: 'Family and Home Words',
      titleFa: 'واژگان خانواده و خانه',
      instruction:
        'این واژگان برای معرفی اعضای خانواده و محیط خانه استفاده می شود. هر واژه را با مثال کوتاه حفظ کنید.',
      items: [
        { en: 'Mother', fa: 'مادر', example: 'My mother cooks dinner.' },
        { en: 'Father', fa: 'پدر', example: 'My father works nearby.' },
        { en: 'Child', fa: 'کودک', example: 'The child is sleeping.' },
        { en: 'Kitchen', fa: 'آشپزخانه', example: 'Tea is in the kitchen.' },
        { en: 'Living room', fa: 'اتاق نشیمن', example: 'We talk in the living room.' },
      ],
      exercisePrompt: 'کدام گزینه معنی «Kitchen» است؟',
      exerciseOptions: ['اتاق خواب', 'آشپزخانه', 'بالکن', 'پارک'],
      exerciseAnswer: 'آشپزخانه',
    },
    reading: {
      title: 'Reading: Leila Introduces Her Family',
      titleFa: 'خواندن: لیلا خانواده اش را معرفی می کند',
      instruction:
        'متن را با تمرکز روی اطلاعات اصلی بخوانید: چه کسی، کجا و چه کاری انجام می دهد. سپس سوال درک مطلب را پاسخ دهید.',
      content:
        'Leila lives with her parents and two children in Mashhad. She wakes up at 6:30 and prepares breakfast. Her son goes to school at 7:30. Her daughter stays home with her grandmother. In the evening, the family eats dinner together and talks about the day.',
      prompt: 'دختر لیلا روزها با چه کسی می ماند؟',
      options: ['با همسایه', 'با مادربزرگ', 'در مدرسه', 'تنها در خانه'],
      answer: 'با مادربزرگ',
    },
    grammarTwo: {
      title: 'Possessive Adjectives',
      titleFa: 'صفت ملکی',
      instruction:
        'برای نشان دادن مالکیت از my, your, his, her, our, their استفاده می کنیم. به جای اسم تکراری از صفت ملکی استفاده کنید.',
      content:
        'my book — کتاب من\nyour bag — کیف تو\nher phone — گوشی او\nour class — کلاس ما\n\nمثال: This is my pen. That is her notebook.',
      prompt: 'This is ___ sister. (for I)',
      answer: 'my',
    },
    writing: {
      title: 'Writing: Personal Introduction',
      titleFa: 'نوشتن: معرفی شخصی',
      instruction:
        '۴ تا ۶ جمله درباره خودتان بنویسید. هدف این تمرین ساخت جمله ساده و واضح است، نه نوشتن متن پیچیده.',
      content:
        'نمونه: My name is Zahra. I am 38 years old. I live in Karaj. I have two children. I like walking in the evening.',
      prompt: 'خودتان را معرفی کنید و حداقل به نام، شهر محل زندگی و یک علاقه اشاره کنید.',
      hints: ['My name is ...', 'I live in ...', 'I have ...', 'I like ...'],
    },
    review: {
      title: 'Weekly Review 1',
      titleFa: 'مرور هفتگی ۱',
      content:
        'این هفته سلام و احوال پرسی، فعل to be و صفت ملکی را یاد گرفتید. تفاوت am/is/are را دوباره مرور کنید.',
      prompt: 'جمله درست را انتخاب کنید:',
      options: ['She are my friend.', 'She is my friend.', 'She am my friend.', 'She be my friend.'],
      answer: 'She is my friend.',
    },
  },
  {
    week: 2,
    themeFa: 'زمان بندی و مسیرهای شهری',
    vocabOne: {
      title: 'Time and Weekdays',
      titleFa: 'زمان و روزهای هفته',
      instruction:
        'این واژگان برای تنظیم برنامه روزانه ضروری هستند. ابتدا روزها را به ترتیب حفظ کنید و سپس ساعت ها را تمرین کنید.',
      items: [
        { en: 'Monday', fa: 'دوشنبه', example: 'My class is on Monday.' },
        { en: 'Wednesday', fa: 'چهارشنبه', example: 'We visit my aunt on Wednesday.' },
        { en: 'Weekend', fa: 'آخر هفته', example: 'I rest on the weekend.' },
        { en: 'Morning', fa: 'صبح', example: 'I study in the morning.' },
        { en: 'At 8 o clock', fa: 'ساعت ۸', example: 'The bus leaves at 8 o clock.' },
      ],
      exercisePrompt: '«Weekend» یعنی چه؟',
      exerciseOptions: ['صبح زود', 'آخر هفته', 'روز کاری', 'نیمه شب'],
      exerciseAnswer: 'آخر هفته',
    },
    grammarOne: {
      title: 'Present Simple Positive and Negative',
      titleFa: 'حال ساده مثبت و منفی',
      instruction:
        'برای عادت های روزانه از حال ساده استفاده می کنیم. در منفی کردن با I/you/we/they از do not و با he/she از does not استفاده می شود.',
      content:
        'I work every day.\nShe works every day.\nI do not work on Friday.\nShe does not work on Friday.\n\nنکته: برای he/she در حالت مثبت به فعل s اضافه می شود.',
      prompt: 'He ___ coffee in the morning. (drink)',
      answer: 'drinks',
    },
    vocabTwo: {
      title: 'City Places',
      titleFa: 'مکان های شهری',
      instruction:
        'این واژگان برای پرسیدن آدرس و توضیح مسیر استفاده می شود. هر کلمه را در یک جمله مرتبط با شهر خود به کار ببرید.',
      items: [
        { en: 'Hospital', fa: 'بیمارستان', example: 'The hospital is near my home.' },
        { en: 'Pharmacy', fa: 'داروخانه', example: 'I buy medicine at the pharmacy.' },
        { en: 'Bus stop', fa: 'ایستگاه اتوبوس', example: 'Wait at the bus stop.' },
        { en: 'Bank', fa: 'بانک', example: 'The bank opens at 8.' },
        { en: 'Market', fa: 'بازار', example: 'We shop at the market.' },
      ],
      exercisePrompt: '«Bus stop» چه معنی دارد؟',
      exerciseOptions: ['پارکینگ', 'ایستگاه اتوبوس', 'خیابان اصلی', 'ایستگاه قطار'],
      exerciseAnswer: 'ایستگاه اتوبوس',
    },
    reading: {
      title: 'Reading: Bus Schedule Note',
      titleFa: 'خواندن: یادداشت زمان بندی اتوبوس',
      instruction:
        'به زمان ها و مقصدها دقت کنید. این نوع متن در زندگی روزمره بسیار کاربردی است.',
      content:
        'The bus from Azadi Square to Valiasr Street leaves at 7:15, 8:00, and 8:45 in the morning. On Thursdays, there is an extra bus at 9:15. Tickets can be bought at the small kiosk near platform 2.',
      prompt: 'اتوبوس اضافه چه روزی وجود دارد؟',
      options: ['شنبه', 'دوشنبه', 'پنجشنبه', 'جمعه'],
      answer: 'پنجشنبه',
    },
    grammarTwo: {
      title: 'Wh Questions with Do and Does',
      titleFa: 'سوال سازی با do و does',
      instruction:
        'برای سوال در زمان حال ساده از do/does استفاده می کنیم. با he/she از does و با بقیه ضمایر از do استفاده کنید.',
      content:
        'Where do you work?\nWhat time does she start?\nWhen do they arrive?\n\nنکته: بعد از does فعل ساده می آید.',
      prompt: 'Where ___ your son study?',
      answer: 'does',
    },
    writing: {
      title: 'Writing: My Weekly Plan',
      titleFa: 'نوشتن: برنامه هفتگی من',
      instruction:
        'یک برنامه کوتاه برای سه روز هفته بنویسید. از روز هفته، ساعت و فعالیت استفاده کنید.',
      content:
        'نمونه: On Monday at 9, I go to work. On Wednesday evening, I visit my mother. On Friday, I rest at home.',
      prompt: 'سه جمله درباره برنامه هفتگی خودتان بنویسید.',
      hints: ['On ...', 'At ...', 'I ...', 'In the evening ...'],
    },
    review: {
      title: 'Weekly Review 2',
      titleFa: 'مرور هفتگی ۲',
      content:
        'در این هفته روزهای هفته، مکان های شهری و سوال سازی با do/does را تمرین کردید.',
      prompt: 'کدام سوال درست است؟',
      options: ['Where does you work?', 'Where do you work?', 'Where you do work?', 'Where do works you?'],
      answer: 'Where do you work?',
    },
  },
  {
    week: 3,
    themeFa: 'غذا و سلامت',
    vocabOne: {
      title: 'Food and Grocery Words',
      titleFa: 'واژگان غذا و خرید روزانه',
      instruction:
        'این واژگان برای خرید و سفارش غذا ضروری هستند. به جمع و مفرد بودن کلمات هم توجه کنید.',
      items: [
        { en: 'Bread', fa: 'نان', example: 'Please buy fresh bread.' },
        { en: 'Rice', fa: 'برنج', example: 'We cook rice for lunch.' },
        { en: 'Vegetables', fa: 'سبزیجات', example: 'Vegetables are healthy.' },
        { en: 'Chicken', fa: 'مرغ', example: 'Chicken is in the fridge.' },
        { en: 'Bottle of water', fa: 'بطری آب', example: 'Take a bottle of water.' },
      ],
      exercisePrompt: '«Vegetables» یعنی چه؟',
      exerciseOptions: ['میوه ها', 'سبزیجات', 'حبوبات', 'ادویه'],
      exerciseAnswer: 'سبزیجات',
    },
    grammarOne: {
      title: 'Some and Any',
      titleFa: 'کاربرد some و any',
      instruction:
        'برای جمله مثبت معمولا some و برای منفی/سوالی معمولا any می آید. این الگو برای مواد غذایی بسیار رایج است.',
      content:
        'There is some milk in the fridge.\nDo we have any eggs?\nThere are not any apples.\n\nنکته: some بیشتر در جملات مثبت استفاده می شود.',
      prompt: 'Do you have ___ tea?',
      answer: 'any',
    },
    vocabTwo: {
      title: 'Health and Body',
      titleFa: 'واژگان سلامت و بدن',
      instruction:
        'واژه های این درس برای گفت و گو در داروخانه، درمانگاه و خانه کاربرد دارد.',
      items: [
        { en: 'Headache', fa: 'سردرد', example: 'I have a headache today.' },
        { en: 'Fever', fa: 'تب', example: 'My child has a fever.' },
        { en: 'Medicine', fa: 'دارو', example: 'Take your medicine after lunch.' },
        { en: 'Rest', fa: 'استراحت', example: 'You need rest.' },
        { en: 'Appointment', fa: 'نوبت ملاقات', example: 'I have a doctor appointment.' },
      ],
      exercisePrompt: '«Appointment» در این درس چه معنی دارد؟',
      exerciseOptions: ['وقت ملاقات', 'نسخه پزشک', 'فشار خون', 'زخم'],
      exerciseAnswer: 'وقت ملاقات',
    },
    reading: {
      title: 'Reading: At the Clinic',
      titleFa: 'خواندن: در درمانگاه',
      instruction:
        'این متن یک گفت و گوی کوتاه در درمانگاه است. به کلمات کلیدی مربوط به علائم و توصیه پزشک دقت کنید.',
      content:
        'Mina goes to a clinic because she has a fever and sore throat. The doctor checks her temperature and asks her to drink warm water, take medicine twice a day, and rest for two days. Mina asks if she can go to work tomorrow. The doctor says, "Stay home and recover first."',
      prompt: 'پزشک چند بار در روز مصرف دارو را پیشنهاد می کند؟',
      options: ['یک بار', 'دو بار', 'سه بار', 'فقط شب'],
      answer: 'دو بار',
    },
    grammarTwo: {
      title: 'Giving Advice with Should',
      titleFa: 'توصیه کردن با should',
      instruction:
        'برای توصیه مودبانه از should و برای توصیه منفی از should not استفاده می کنیم.',
      content:
        'You should drink more water.\nHe should rest today.\nYou should not skip breakfast.\n\nنکته: بعد از should فعل ساده می آید.',
      prompt: 'You should ___ early tonight. (sleep)',
      answer: 'sleep',
    },
    writing: {
      title: 'Writing: Healthy Day Plan',
      titleFa: 'نوشتن: برنامه یک روز سالم',
      instruction:
        'یک برنامه کوتاه سلامتی بنویسید: غذا، آب، حرکت بدنی و خواب. سعی کنید از should هم استفاده کنید.',
      content:
        'نمونه: I should drink eight glasses of water. I should walk for thirty minutes. I should sleep before 11.',
      prompt: '۴ تا ۵ جمله درباره برنامه سالم روزانه خودتان بنویسید.',
      hints: ['I should ...', 'I eat ...', 'I drink ...', 'I sleep ...'],
    },
    review: {
      title: 'Weekly Review 3',
      titleFa: 'مرور هفتگی ۳',
      content:
        'در این هفته واژگان غذا و سلامت، و ساختار some/any و should را تمرین کردید.',
      prompt: 'جمله درست را انتخاب کنید:',
      options: ['There is any rice.', 'There are some rice.', 'There is some rice.', 'There any rice is.'],
      answer: 'There is some rice.',
    },
  },
  {
    week: 4,
    themeFa: 'کار، مطالعه و مهارت های روزمره',
    vocabOne: {
      title: 'Jobs and Workplaces',
      titleFa: 'شغل ها و محیط کار',
      instruction:
        'این واژگان برای صحبت درباره شغل و محل کار مفید هستند. تلاش کنید هر واژه را به زندگی خودتان ربط دهید.',
      items: [
        { en: 'Nurse', fa: 'پرستار', example: 'My sister is a nurse.' },
        { en: 'Teacher', fa: 'معلم', example: 'Our teacher is patient.' },
        { en: 'Office', fa: 'اداره', example: 'I work in an office.' },
        { en: 'Shift', fa: 'شیفت کاری', example: 'He has a night shift.' },
        { en: 'Colleague', fa: 'همکار', example: 'My colleague helps me.' },
      ],
      exercisePrompt: '«Colleague» یعنی چه؟',
      exerciseOptions: ['مشتری', 'همکار', 'مدیر', 'کارآموز'],
      exerciseAnswer: 'همکار',
    },
    grammarOne: {
      title: 'Can and Cannot',
      titleFa: 'توانایی با can و cannot',
      instruction:
        'برای بیان توانایی یا امکان از can استفاده می کنیم. برای ناتوانی یا عدم امکان از cannot استفاده می شود.',
      content:
        'I can type fast.\nShe can speak English.\nHe cannot drive.\nCan you help me?\n\nنکته: بعد از can فعل بدون تغییر می آید.',
      prompt: 'She can ___ emails quickly. (write)',
      answer: 'write',
    },
    vocabTwo: {
      title: 'Study and Office Actions',
      titleFa: 'واژگان کارهای اداری و درسی',
      instruction:
        'این واژه ها در محیط کار و کلاس بسیار تکرار می شوند. با آن ها جمله کوتاه بسازید.',
      items: [
        { en: 'Print', fa: 'چاپ کردن', example: 'Please print this file.' },
        { en: 'Submit', fa: 'ارسال کردن', example: 'Submit the form today.' },
        { en: 'Attend', fa: 'شرکت کردن', example: 'I attend the meeting online.' },
        { en: 'Review', fa: 'مرور کردن', example: 'Review your notes.' },
        { en: 'Deadline', fa: 'مهلت نهایی', example: 'The deadline is tomorrow.' },
      ],
      exercisePrompt: '«Deadline» به چه معنی است؟',
      exerciseOptions: ['زمان استراحت', 'مهلت نهایی', 'جلسه فوری', 'دعوت نامه'],
      exerciseAnswer: 'مهلت نهایی',
    },
    reading: {
      title: 'Reading: Message from a Colleague',
      titleFa: 'خواندن: پیام یک همکار',
      instruction:
        'پیام را بخوانید و به کارهایی که باید انجام شود دقت کنید. این سبک متن در محل کار رایج است.',
      content:
        'Hi Sara, please review the sales report before 3 PM and submit your comments in the shared file. At 4 PM, we have a short meeting with the manager. If you can, print two copies of the final chart for the meeting room. Thanks, Reza.',
      prompt: 'سارا باید چه ساعتی در جلسه حاضر شود؟',
      options: ['۳ بعدازظهر', '۴ بعدازظهر', '۵ بعدازظهر', '۹ صبح'],
      answer: '۴ بعدازظهر',
    },
    grammarTwo: {
      title: 'Adverbs of Frequency',
      titleFa: 'قیدهای تکرار',
      instruction:
        'برای بیان عادت از always, usually, sometimes, never استفاده می کنیم. جای این قیدها قبل از فعل اصلی است.',
      content:
        'I always check my email.\nShe usually arrives early.\nThey sometimes eat outside.\nHe never drinks coffee.\n\nنکته: با فعل to be، قید بعد از am/is/are می آید.',
      prompt: 'He ___ studies at night. (usually)',
      answer: 'usually',
    },
    writing: {
      title: 'Writing: Short Work Email',
      titleFa: 'نوشتن: ایمیل کاری کوتاه',
      instruction:
        'یک ایمیل کوتاه و مودبانه بنویسید: سلام، درخواست یا اطلاع، پایان بندی.',
      content:
        'نمونه: Hello Mr. Ahmadi, I reviewed the document and added my notes. I can join the meeting at 4 PM. Best regards, Mina.',
      prompt: 'یک ایمیل ۴ جمله ای برای همکار خود درباره یک کار روزانه بنویسید.',
      hints: ['Hello ...', 'I reviewed ...', 'I can ...', 'Best regards, ...'],
    },
    review: {
      title: 'Weekly Review 4',
      titleFa: 'مرور هفتگی ۴',
      content:
        'این هفته روی واژگان محیط کار، can/cannot و قیدهای تکرار تمرکز داشتید.',
      prompt: 'کدام جمله صحیح است؟',
      options: ['She can to help me.', 'She can helps me.', 'She can help me.', 'She can helping me.'],
      answer: 'She can help me.',
    },
  },
  {
    week: 5,
    themeFa: 'سفر و خدمات شهری',
    vocabOne: {
      title: 'Travel and Transport',
      titleFa: 'واژگان سفر و حمل و نقل',
      instruction:
        'این واژگان را برای سفرهای درون شهری و بین شهری تمرین کنید. کنار هر واژه یک موقعیت واقعی تصور کنید.',
      items: [
        { en: 'Ticket', fa: 'بلیط', example: 'I bought a bus ticket.' },
        { en: 'Platform', fa: 'سکو', example: 'Wait at platform 4.' },
        { en: 'Departure', fa: 'حرکت', example: 'Departure is at 10:20.' },
        { en: 'Luggage', fa: 'چمدان', example: 'My luggage is heavy.' },
        { en: 'Reservation', fa: 'رزرو', example: 'I have a hotel reservation.' },
      ],
      exercisePrompt: '«Luggage» یعنی چه؟',
      exerciseOptions: ['بار و چمدان', 'گذرنامه', 'ایستگاه', 'هتل'],
      exerciseAnswer: 'بار و چمدان',
    },
    grammarOne: {
      title: 'Past To Be: Was and Were',
      titleFa: 'گذشته فعل to be',
      instruction:
        'برای صحبت درباره گذشته از was و were استفاده می کنیم. I/he/she با was و you/we/they با were می آید.',
      content:
        'I was tired yesterday.\nShe was in Tabriz last week.\nWe were at the station.\nThey were late.\n\nنکته: در سوال، was/were به ابتدای جمله می رود.',
      prompt: 'They ___ at the hotel last night.',
      answer: 'were',
    },
    vocabTwo: {
      title: 'Hotel and Shopping Words',
      titleFa: 'واژگان هتل و خرید',
      instruction:
        'این مجموعه برای اقامت و خرید در سفر مفید است. سعی کنید عبارت ها را در قالب دیالوگ کوتاه تمرین کنید.',
      items: [
        { en: 'Reception', fa: 'پذیرش', example: 'Ask at reception.' },
        { en: 'Single room', fa: 'اتاق یک نفره', example: 'I need a single room.' },
        { en: 'Receipt', fa: 'رسید', example: 'Please keep your receipt.' },
        { en: 'Discount', fa: 'تخفیف', example: 'Is there any discount?' },
        { en: 'Exchange', fa: 'تعویض کردن', example: 'Can I exchange this shirt?' },
      ],
      exercisePrompt: '«Receipt» چه معنی دارد؟',
      exerciseOptions: ['کیف پول', 'رسید خرید', 'کارت ورود', 'برچسب قیمت'],
      exerciseAnswer: 'رسید خرید',
    },
    reading: {
      title: 'Reading: A Short Trip Diary',
      titleFa: 'خواندن: یادداشت کوتاه سفر',
      instruction:
        'متن را بخوانید و روی افعال گذشته تمرکز کنید. سپس سوال را پاسخ دهید.',
      content:
        'Last weekend, we were in Isfahan for two days. Our hotel was near the old bridge, so we walked there at night. On Saturday morning, we visited a local market and bought handmade gifts. In the evening, we were tired but happy.',
      prompt: 'هتل آن ها نزدیک چه مکانی بود؟',
      options: ['فرودگاه', 'پل قدیمی', 'ایستگاه قطار', 'پارک'],
      answer: 'پل قدیمی',
    },
    grammarTwo: {
      title: 'Past Simple Regular Verbs',
      titleFa: 'گذشته ساده افعال با قاعده',
      instruction:
        'برای بسیاری از افعال، با اضافه کردن ed گذشته ساده ساخته می شود. به تلفظ انتهای کلمه دقت کنید.',
      content:
        'I visited my aunt.\nShe booked a room.\nWe walked to the station.\nThey arrived late.\n\nنکته: در سوال و منفی، did استفاده می شود و فعل ساده می آید.',
      prompt: 'Yesterday we ___ our tickets online. (book)',
      answer: 'booked',
    },
    writing: {
      title: 'Writing: My Last Weekend',
      titleFa: 'نوشتن: آخر هفته گذشته من',
      instruction:
        '۵ جمله درباره آخر هفته گذشته بنویسید. حداقل از دو فعل گذشته استفاده کنید.',
      content:
        'نمونه: Last Friday, I visited my sister. We cooked dinner together and watched a movie. It was a calm evening.',
      prompt: 'درباره آخر هفته گذشته خودتان با زمان گذشته ساده بنویسید.',
      hints: ['Last ...', 'I was ...', 'I visited ...', 'We ...ed ...'],
    },
    review: {
      title: 'Weekly Review 5',
      titleFa: 'مرور هفتگی ۵',
      content:
        'در این هفته واژگان سفر، was/were و گذشته ساده افعال با قاعده را تمرین کردید.',
      prompt: 'جمله درست را انتخاب کنید:',
      options: ['We was at home.', 'We were at home.', 'We are at home yesterday.', 'We be at home.'],
      answer: 'We were at home.',
    },
  },
  {
    week: 6,
    themeFa: 'زندگی دیجیتال',
    vocabOne: {
      title: 'Internet and Devices',
      titleFa: 'واژگان اینترنت و ابزار دیجیتال',
      instruction:
        'این واژگان را برای کارهای آنلاین و آموزش مجازی یاد بگیرید. هر کلمه را در یک موقعیت روزمره تصور کنید.',
      items: [
        { en: 'Wi-Fi', fa: 'وای فای', example: 'The Wi-Fi is slow today.' },
        { en: 'Password', fa: 'رمز عبور', example: 'Change your password monthly.' },
        { en: 'Download', fa: 'دانلود', example: 'Download the file first.' },
        { en: 'Upload', fa: 'آپلود', example: 'Upload your homework.' },
        { en: 'Link', fa: 'لینک', example: 'Send me the class link.' },
      ],
      exercisePrompt: '«Upload» یعنی چه؟',
      exerciseOptions: ['باز کردن برنامه', 'بارگذاری فایل', 'ذخیره روی کاغذ', 'خاموش کردن سیستم'],
      exerciseAnswer: 'بارگذاری فایل',
    },
    grammarOne: {
      title: 'Present Continuous',
      titleFa: 'زمان حال استمراری',
      instruction:
        'برای کاری که همین الان در حال انجام است از am/is/are + verb-ing استفاده می کنیم.',
      content:
        'I am studying now.\nShe is sending an email.\nThey are waiting for the teacher.\n\nنکته: به فعل اصلی ing اضافه می شود.',
      prompt: 'He is ___ a video now. (watch)',
      answer: 'watching',
    },
    vocabTwo: {
      title: 'Online Safety Vocabulary',
      titleFa: 'واژگان امنیت آنلاین',
      instruction:
        'برای کار امن در اینترنت، این واژگان را بلد باشید. معنی هر واژه را همراه با مثال مرور کنید.',
      items: [
        { en: 'Scam', fa: 'کلاهبرداری اینترنتی', example: 'That message is a scam.' },
        { en: 'Verification code', fa: 'کد تایید', example: 'Do not share your verification code.' },
        { en: 'Privacy', fa: 'حریم خصوصی', example: 'Check your privacy settings.' },
        { en: 'Secure', fa: 'امن', example: 'Use a secure website.' },
        { en: 'Report', fa: 'گزارش کردن', example: 'Report suspicious accounts.' },
      ],
      exercisePrompt: '«Verification code» چه معنی دارد؟',
      exerciseOptions: ['کد تایید', 'کد تخفیف', 'شماره سفارش', 'شناسه کاربر'],
      exerciseAnswer: 'کد تایید',
    },
    reading: {
      title: 'Reading: Online Class Instructions',
      titleFa: 'خواندن: راهنمای کلاس آنلاین',
      instruction:
        'متن راهنما را بخوانید و به دستورالعمل های مهم توجه کنید.',
      content:
        'Please join the class five minutes early. Test your microphone before the lesson starts. Keep your camera on during discussion time. If your connection drops, rejoin using the same link. Upload your assignment by 8 PM in the class portal.',
      prompt: 'تکلیف تا چه ساعتی باید آپلود شود؟',
      options: ['۶ عصر', '۷ عصر', '۸ شب', '۹ شب'],
      answer: '۸ شب',
    },
    grammarTwo: {
      title: 'Comparative Adjectives',
      titleFa: 'صفت های مقایسه ای',
      instruction:
        'برای مقایسه دو چیز از صفت های مقایسه ای استفاده می کنیم: faster, easier, more useful.',
      content:
        'This laptop is faster than my old one.\nOnline payment is easier than cash payment.\nThis app is more useful for students.\n\nنکته: برای صفت های کوتاه معمولا er اضافه می شود.',
      prompt: 'This task is ___ than yesterday. (easy)',
      answer: 'easier',
    },
    writing: {
      title: 'Writing: Request for Technical Help',
      titleFa: 'نوشتن: درخواست کمک فنی',
      instruction:
        'یک پیام مودبانه برای پشتیبانی بنویسید و مشکل را دقیق توضیح دهید.',
      content:
        'نمونه: Hello support team, I am trying to upload my file, but the page is not loading. I checked my Wi-Fi and restarted the app. Please help me solve this issue.',
      prompt: 'یک پیام ۴ تا ۵ جمله ای برای مشکل اینترنت یا اپلیکیشن بنویسید.',
      hints: ['Hello ...', 'I am trying to ...', 'The problem is ...', 'Please help me ...'],
    },
    review: {
      title: 'Weekly Review 6',
      titleFa: 'مرور هفتگی ۶',
      content:
        'این هفته واژگان دیجیتال، حال استمراری و مقایسه ای ها را تمرین کردید.',
      prompt: 'گزینه درست را انتخاب کنید:',
      options: ['She is upload the file.', 'She uploading the file.', 'She is uploading the file.', 'She is uploads the file.'],
      answer: 'She is uploading the file.',
    },
  },
  {
    week: 7,
    themeFa: 'جامعه، خدمات و برنامه آینده',
    vocabOne: {
      title: 'Community Services',
      titleFa: 'خدمات اجتماعی',
      instruction:
        'این واژگان برای تعامل با خدمات شهری و اجتماعی کاربرد دارد.',
      items: [
        { en: 'Municipality', fa: 'شهرداری', example: 'The municipality repaired the street.' },
        { en: 'Library', fa: 'کتابخانه', example: 'My children study at the library.' },
        { en: 'Community center', fa: 'مرکز محله', example: 'There is a class at the community center.' },
        { en: 'Volunteer', fa: 'داوطلب', example: 'She works as a volunteer.' },
        { en: 'Service desk', fa: 'میز خدمات', example: 'Ask at the service desk.' },
      ],
      exercisePrompt: '«Volunteer» یعنی چه؟',
      exerciseOptions: ['کارمند رسمی', 'داوطلب', 'مسئول ثبت نام', 'نگهبان'],
      exerciseAnswer: 'داوطلب',
    },
    grammarOne: {
      title: 'Future with Going To',
      titleFa: 'بیان آینده با going to',
      instruction:
        'وقتی از برنامه یا تصمیم آینده صحبت می کنیم از be going to استفاده می شود.',
      content:
        'I am going to visit my aunt tomorrow.\nShe is going to start a new class.\nWe are going to clean the park on Friday.\n\nنکته: بعد از going to فعل ساده می آید.',
      prompt: 'They are going to ___ a workshop next week. (join)',
      answer: 'join',
    },
    vocabTwo: {
      title: 'Events and Hobbies',
      titleFa: 'رویدادها و سرگرمی ها',
      instruction:
        'واژگان این درس برای صحبت درباره اوقات فراغت و برنامه های جمعی است.',
      items: [
        { en: 'Workshop', fa: 'کارگاه', example: 'I joined a cooking workshop.' },
        { en: 'Exhibition', fa: 'نمایشگاه', example: 'The exhibition starts at 5.' },
        { en: 'Registration', fa: 'ثبت نام', example: 'Online registration is open.' },
        { en: 'Practice', fa: 'تمرین', example: 'Daily practice improves skills.' },
        { en: 'Performance', fa: 'اجرا', example: 'The children have a music performance.' },
      ],
      exercisePrompt: '«Registration» چه معنی دارد؟',
      exerciseOptions: ['ثبت نام', 'لغو برنامه', 'امتحان نهایی', 'اتاق تمرین'],
      exerciseAnswer: 'ثبت نام',
    },
    reading: {
      title: 'Reading: Neighborhood Festival',
      titleFa: 'خواندن: جشنواره محلی',
      instruction:
        'اطلاعیه را بخوانید و به زمان، مکان و فعالیت ها دقت کنید.',
      content:
        'Our neighborhood festival is going to take place on Friday evening at the community center. Families can join a small art workshop, a book exchange table, and a healthy food stand. Registration opens at 4:30 PM, and the main program starts at 5 PM.',
      prompt: 'برنامه اصلی چه ساعتی شروع می شود؟',
      options: ['۴ بعدازظهر', '۴:۳۰ بعدازظهر', '۵ بعدازظهر', '۶ بعدازظهر'],
      answer: '۵ بعدازظهر',
    },
    grammarTwo: {
      title: 'Polite Requests with Could and Would',
      titleFa: 'درخواست مودبانه با could و would',
      instruction:
        'برای درخواست مودبانه از Could you ...? و Would you ...? استفاده می کنیم.',
      content:
        'Could you help me with this form?\nWould you send me the address?\nCould I ask a question?\n\nنکته: این ساختارها رسمی تر و مودبانه تر از حالت مستقیم هستند.',
      prompt: '___ you open the window, please?',
      answer: 'Could',
    },
    writing: {
      title: 'Writing: Invitation Message',
      titleFa: 'نوشتن: پیام دعوت',
      instruction:
        'یک پیام دعوت کوتاه برای یک رویداد بنویسید. زمان، مکان و دلیل دعوت را بنویسید.',
      content:
        'نمونه: Hi Maryam, we are going to have a reading club on Thursday at 6 PM in our library. Would you like to join us?',
      prompt: 'یک پیام دعوت ۴ جمله ای برای یک دوست یا همسایه بنویسید.',
      hints: ['Hi ...', 'We are going to ...', 'At ... in ...', 'Would you like to ...?'],
    },
    review: {
      title: 'Weekly Review 7',
      titleFa: 'مرور هفتگی ۷',
      content:
        'این هفته روی برنامه آینده و درخواست مودبانه تمرین کردید.',
      prompt: 'کدام جمله مودبانه و درست است؟',
      options: ['You open the door.', 'Open the door now.', 'Could you open the door, please?', 'Door open you please.'],
      answer: 'Could you open the door, please?',
    },
  },
  {
    week: 8,
    themeFa: 'جمع بندی و اعتماد به نفس در زبان',
    vocabOne: {
      title: 'Academic and IELTS Connectors',
      titleFa: 'واژگان پیوند دهنده برای نوشتار',
      instruction:
        'این کلمات به شما کمک می کند جمله ها را حرفه ای تر به هم وصل کنید. در نوشتار کوتاه از آن ها استفاده کنید.',
      items: [
        { en: 'However', fa: 'با این حال', example: 'However, the cost is high.' },
        { en: 'Therefore', fa: 'بنابراین', example: 'Therefore, we need a plan.' },
        { en: 'In addition', fa: 'علاوه بر این', example: 'In addition, we need more time.' },
        { en: 'For example', fa: 'برای مثال', example: 'For example, online classes are flexible.' },
        { en: 'As a result', fa: 'در نتیجه', example: 'As a result, productivity improved.' },
      ],
      exercisePrompt: '«Therefore» یعنی چه؟',
      exerciseOptions: ['برای مثال', 'بنابراین', 'به طور ناگهانی', 'در گذشته'],
      exerciseAnswer: 'بنابراین',
    },
    grammarOne: {
      title: 'First Conditional Basics',
      titleFa: 'شرطی نوع اول',
      instruction:
        'برای نتیجه آینده در صورت وقوع شرط، از if + حال ساده و will + فعل استفاده می کنیم.',
      content:
        'If I finish early, I will call you.\nIf she studies, she will pass the exam.\nIf we leave now, we will catch the bus.\n\nنکته: در بخش if از will استفاده نکنید.',
      prompt: 'If it rains, we ___ at home. (stay)',
      answer: 'will stay',
    },
    vocabTwo: {
      title: 'Feelings and Goals',
      titleFa: 'واژگان احساس و هدف گذاری',
      instruction:
        'این واژگان برای صحبت درباره انگیزه و مسیر یادگیری زبان کاربرد دارد.',
      items: [
        { en: 'Confident', fa: 'با اعتماد به نفس', example: 'I feel more confident now.' },
        { en: 'Progress', fa: 'پیشرفت', example: 'My progress is steady.' },
        { en: 'Challenge', fa: 'چالش', example: 'Listening is my challenge.' },
        { en: 'Goal', fa: 'هدف', example: 'My goal is clear speaking.' },
        { en: 'Improve', fa: 'بهبود دادن', example: 'I improve by daily practice.' },
      ],
      exercisePrompt: '«Progress» چه معنی دارد؟',
      exerciseOptions: ['پیشرفت', 'توقف', 'مقایسه', 'پیشنهاد'],
      exerciseAnswer: 'پیشرفت',
    },
    reading: {
      title: 'Reading: Learning Reflection',
      titleFa: 'خواندن: بازتاب مسیر یادگیری',
      instruction:
        'این متن درباره مسیر یادگیری زبان است. روی ایده اصلی و دلایل پیشرفت تمرکز کنید.',
      content:
        'At the start of this course, Farideh was nervous about speaking English. She decided to study twenty minutes every day and review new words each weekend. After two months, she can introduce herself clearly, write short messages, and understand simple conversations. She still makes mistakes, but she is confident and motivated.',
      prompt: 'دلیل اصلی پیشرفت فریده چه بود؟',
      options: ['کلاس طولانی در یک روز', 'مطالعه روزانه و مرور هفتگی', 'فقط دیدن فیلم', 'حفظ کردن بدون تمرین'],
      answer: 'مطالعه روزانه و مرور هفتگی',
    },
    grammarTwo: {
      title: 'Mixed Tense Review',
      titleFa: 'مرور زمان های پرکاربرد',
      instruction:
        'در این درس، حال ساده، حال استمراری و گذشته ساده را کنار هم مرور می کنیم تا انتخاب زمان درست را تمرین کنید.',
      content:
        'I usually study at night. (حال ساده)\nI am studying now. (حال استمراری)\nI studied yesterday. (گذشته ساده)\n\nنکته: برای تشخیص زمان به نشانه های زمانی مثل now, usually, yesterday دقت کنید.',
      prompt: 'Yesterday I ___ my lesson notes. (review)',
      answer: 'reviewed',
    },
    writing: {
      title: 'Writing: 8-Week Reflection',
      titleFa: 'نوشتن: گزارش پیشرفت هشت هفته ای',
      instruction:
        'یک متن ۶ تا ۸ جمله ای درباره مسیر یادگیری خود بنویسید: ابتدا سطح شروع، سپس پیشرفت، و در پایان هدف بعدی.',
      content:
        'نمونه: At first, I could not speak confidently. Now I can write short emails and understand daily conversations. My next goal is to improve listening.',
      prompt: 'درباره مسیر ۸ هفته ای خودتان بنویسید و حداقل یک هدف آینده اضافه کنید.',
      hints: ['At first ...', 'Now I can ...', 'I improved in ...', 'My next goal is ...'],
    },
    review: {
      title: 'Final Weekly Review',
      titleFa: 'مرور نهایی هفتگی',
      content:
        'این هفته جمع بندی نهایی دوره بود: واژگان پیوند دهنده، شرطی نوع اول و مرور زمان ها.',
      prompt: 'کدام جمله شرطی درست است؟',
      options: [
        'If I will study, I pass.',
        'If I study, I will pass.',
        'If I studied, I will pass yesterday.',
        'If study I, I will pass.',
      ],
      answer: 'If I study, I will pass.',
    },
  },
];

export const englishLessons: EnglishLesson[] = englishWeekPlans.flatMap(buildWeekLessons);
